import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Snackbar, Alert } from '@mui/material';

// Eagerly loaded -- landing + auth (first paint)
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { AuthProvider, useAuth } from './context/AuthContext';
import TrialBanner from './components/TrialBanner';

// SEO pages — lazy but small
const TmsForSmallFleets = lazy(() => import('./pages/seo/TmsForSmallFleets'));
const DispatchForOwnerOperators = lazy(() => import('./pages/seo/DispatchForOwnerOperators'));
const TmsFor10TruckFleet = lazy(() => import('./pages/seo/TmsFor10TruckFleet'));
const TruckingInvoiceSoftware = lazy(() => import('./pages/seo/TruckingInvoiceSoftware'));
const EdiForSmallCarriers = lazy(() => import('./pages/seo/EdiForSmallCarriers'));
const DispatchSoftware = lazy(() => import('./pages/seo/DispatchSoftware'));
const TruckingDispatchSystem = lazy(() => import('./pages/seo/TruckingDispatchSystem'));
const FleetManagementSoftware = lazy(() => import('./pages/seo/FleetManagementSoftware'));
const LoadTrackingSoftware = lazy(() => import('./pages/seo/LoadTrackingSoftware'));
const RoutePlanningSoftware = lazy(() => import('./pages/seo/RoutePlanningSoftware'));

// Blog pages
const BlogIndex = lazy(() => import('./pages/blog/BlogIndex'));
const HowToChooseTms = lazy(() => import('./pages/blog/HowToChooseTms'));

const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// Heavy app pages — code-split (only loaded after login)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const RouteOptimizer = lazy(() => import('./pages/RouteOptimizer'));
const TripScheduler = lazy(() => import('./pages/TripScheduler'));
const Fleet = lazy(() => import('./pages/Fleet'));
const Drivers = lazy(() => import('./pages/Drivers'));
const HoursOfService = lazy(() => import('./pages/HoursOfService'));
const Customers = lazy(() => import('./pages/Customers'));
const Routes = lazy(() => import('./pages/Routes'));
const Reports = lazy(() => import('./pages/Reports'));
const Dispatch = lazy(() => import('./pages/Dispatch'));
const Settings = lazy(() => import('./pages/Settings'));
const LoadBoardImport = lazy(() => import('./pages/LoadBoardImport'));
const MasterAdmin = lazy(() => import('./pages/MasterAdmin'));
const FleetGuard = lazy(() => import('./pages/FleetGuard'));

// Loading spinner for lazy chunks
function LazyFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#ffffff' }}>
      <CircularProgress sx={{ color: '#2563eb' }} />
    </Box>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function PrivateRoute({ children }) {
  const { user, loading, accountLocked } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;

  // If account is locked (trial expired, no payment), redirect to billing
  // Allow access to /settings so they can actually upgrade
  if (accountLocked && !location.pathname.startsWith('/settings')) {
    return <Navigate to="/settings?tab=billing" replace />;
  }

  return (
    <>
      <TrialBanner />
      {children}
    </>
  );
}

function MasterAdminRoute({ children }) {
  const { user, loading, isMasterAdmin } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!isMasterAdmin) return <Navigate to="/dashboard" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading, isMasterAdmin } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={isMasterAdmin ? '/admin' : '/dashboard'} />;
  return children;
}

function App() {
  const [trialMsg, setTrialMsg] = useState('');

  const handleTrialReadOnly = useCallback((e) => {
    setTrialMsg(e.detail?.message || 'Your free trial has ended. Upgrade to continue making changes.');
  }, []);

  useEffect(() => {
    window.addEventListener('trial-read-only', handleTrialReadOnly);
    window.addEventListener('account-expired', handleTrialReadOnly);
    return () => {
      window.removeEventListener('trial-read-only', handleTrialReadOnly);
      window.removeEventListener('account-expired', handleTrialReadOnly);
    };
  }, [handleTrialReadOnly]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Snackbar
        open={!!trialMsg}
        autoHideDuration={6000}
        onClose={() => setTrialMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setTrialMsg('')} severity="warning" variant="filled" sx={{ width: '100%' }}>
          {trialMsg}
        </Alert>
      </Snackbar>
      <AuthProvider>
        <Router>
          <Suspense fallback={<LazyFallback />}>
          <RouterRoutes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/routes"
              element={
                <PrivateRoute>
                  <Routes />
                </PrivateRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PrivateRoute>
                  <Reports />
                </PrivateRoute>
              }
            />
            <Route
              path="/optimize"
              element={
                <PrivateRoute>
                  <RouteOptimizer />
                </PrivateRoute>
              }
            />
            <Route
              path="/scheduler"
              element={
                <PrivateRoute>
                  <TripScheduler />
                </PrivateRoute>
              }
            />
            <Route
              path="/dispatch"
              element={
                <PrivateRoute>
                  <Dispatch />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet"
              element={
                <PrivateRoute>
                  <Fleet />
                </PrivateRoute>
              }
            />
            <Route
              path="/drivers"
              element={
                <PrivateRoute>
                  <Drivers />
                </PrivateRoute>
              }
            />
            <Route
              path="/hours-of-service"
              element={
                <PrivateRoute>
                  <HoursOfService />
                </PrivateRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <PrivateRoute>
                  <Customers />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/import-loads"
              element={
                <PrivateRoute>
                  <LoadBoardImport />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet-guard"
              element={
                <PrivateRoute>
                  <FleetGuard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <MasterAdminRoute>
                  <MasterAdmin />
                </MasterAdminRoute>
              }
            />
            {/* SEO Landing Pages */}
            <Route path="/tms-for-small-fleets" element={<TmsForSmallFleets />} />
            <Route path="/dispatch-software-for-owner-operators" element={<DispatchForOwnerOperators />} />
            <Route path="/tms-for-10-truck-fleet" element={<TmsFor10TruckFleet />} />
            <Route path="/trucking-invoice-software" element={<TruckingInvoiceSoftware />} />
            <Route path="/edi-for-small-carriers" element={<EdiForSmallCarriers />} />
            <Route path="/dispatch-software" element={<DispatchSoftware />} />
            <Route path="/trucking-dispatch-system" element={<TruckingDispatchSystem />} />
            <Route path="/fleet-management-software" element={<FleetManagementSoftware />} />
            <Route path="/load-tracking-software" element={<LoadTrackingSoftware />} />
            <Route path="/route-planning-software" element={<RoutePlanningSoftware />} />
            {/* Blog */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/how-to-choose-tms-small-trucking-company" element={<HowToChooseTms />} />
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="*" element={<Navigate to="/" />} />
          </RouterRoutes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
